/**
 * The brand of an installation of the restaurant product.
 *
 * A brand is configuration, never code: the same code reads whichever brand
 * the installation states. Secrets never belong here (the validator refuses
 * them) — they stay in the installation's environment.
 */
export type BrandId = string;

export interface BrandColors {
  primary: string;
  primaryDark: string;
  accent: string;
  background: string;
  text: string;
}

/** File names under `packages/brand/assets/<brand id>/`. */
export interface BrandAssets {
  logo: string;
  logoDark: string;
  icon: string;
  favicon: string;
  emailHeader: string;
  splash: string;
}

/** Bare hostnames, no scheme and no path. */
export interface BrandDomains {
  console: string;
  guest: string;
  admin: string;
  marketing: string;
  /** Where the backend API answers (the staff app's fallback when its build URL is missing). */
  api: string;
  /** Development installation, when the brand has one. */
  devApi?: string;
  devConsole?: string;
}

export interface BrandEmail {
  fromName: string;
  fromAddress: string;
  staffFromAddress: string;
  stockFromAddress: string;
  replyTo: string;
  supportAddress: string;
}

/** The company that provides the service under this brand. */
export interface BrandCompany {
  legalName: string;
  address: string;
  vatOrRegistrationNumber: string;
  /** ISO 3166-1 alpha-2. */
  country: string;
  privacyControllerEmail: string;
}

export interface BrandLegal {
  termsUrl: string;
  privacyUrl: string;
  cookieUrl?: string;
}

/** Defaults for new fiscal-provider provisioning only; existing systems keep theirs. */
export interface BrandFiscal {
  softwareName: string;
  producerName: string;
}

export interface BrandStores {
  iosUrl?: string;
  androidUrl?: string;
}

/** Store and build identity of the staff app. */
export interface BrandApp {
  slug: string;
  bundleId: string;
  androidPackage: string;
  easProjectId: string;
  easOwner: string;
  updatesUrl: string;
  sentryProject: string;
}

/**
 * Languages every surface of the product offers. The starting language of an
 * installation must be one of them.
 */
export const SUPPORTED_LANGUAGES = ['it', 'en'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

/**
 * Feature 298 — where the marketing site's demo requests go. Optional: an
 * installation whose brand has no sales block exposes no demo-request route.
 */
export interface BrandSales {
  demoRequestInbox: string;
  /** Origins of the marketing site allowed to submit (https, no path). */
  marketingOrigins: string[];
}

export interface BrandConfig {
  id: BrandId;
  /**
   * The language every surface falls back to when nothing about the reader is
   * known: no stored choice, no remembered choice, a browser or device asking
   * for a language the product does not offer. Never the language of the
   * source texts, which stays Italian.
   */
  defaultLanguage: SupportedLanguage;
  productName: string;
  shortName: string;
  staffAppName: string;
  kitchenDisplayName: string;
  voiceWakePhrase: string;
  colors: BrandColors;
  fonts: { heading: string; body: string };
  assets: BrandAssets;
  domains: BrandDomains;
  email: BrandEmail;
  support: { email: string; phone?: string; url?: string };
  company: BrandCompany;
  legal: BrandLegal;
  fiscal: BrandFiscal;
  stores: BrandStores;
  app: BrandApp;
  sales?: BrandSales;
}

/**
 * Fields that may hold the literal "TODO" until they are known: company and
 * legal before the first real venue, store links before the public listing,
 * app identity before the store project exists (an app build refuses TODO).
 */
export const GO_LIVE_SECTIONS = ['company', 'legal', 'stores', 'app'] as const;

/** The value a go-live field holds while it is not known yet. */
export const PENDING = 'TODO';
